import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import { AutoComplete } from '@openx/components-core/lib/AutoComplete/AutoComplete';
import { Button } from '@openx/components-core/lib/Button/Button';
import type { RetargetingDisposition, RetargetingFormState } from '@openx/types/targeting/retargeting';
import { noop } from '@openx/utils/noop';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../utils/context/TargetingContext';
import { DrawerTopBox } from '../shared/TargetingDrawer/DrawerTopBox';
import { retargetingDesignationLabels, retargetingDesignationOptions } from './constants';

const AQUA_UI_CREATE_RETARGETING_SEGMENT_PATH = '/direct/retargeting-segments/new';

type TopBoxProps = {
  accountUid?: string;
  params: RetargetingFormState;
  onDesignationChange: (disposition: RetargetingDisposition | '') => void;
  onSegmentChange: (segmentId: string | null) => void;
};

export const TopBox = ({ accountUid, params, onDesignationChange, onSegmentChange }: TopBoxProps) => {
  const { t } = useTranslation();
  const { useRetargetingSegmentsOptionsFetch = noop } = useTargetingContext();

  const { isLoading, segments, segmentsList } = useRetargetingSegmentsOptionsFetch({
    accountUid,
    disposition: params.disposition,
    enabled: Boolean(params.disposition),
  }) || { isLoading: false, segments: {}, segmentsList: [] };

  const isSegmentDisabled = !params.disposition;

  const segmentAutocomplete = (
    <AutoComplete
      key={`retargeting-segment-${params.disposition}-${params.segment_id}`}
      textFieldProps={{
        label: t('Choose Segment'),
        placeholder: t('[ Select Retargeting Segment ]'),
      }}
      options={segmentsList}
      fullWidth
      loading={isLoading}
      disabled={isSegmentDisabled}
      renderOptions={{
        dataTest: 'retargeting-segment-option',
        tooltip: true,
      }}
      getOptionLabel={option => segments[option]?.name || option}
      onChange={(_e, value) => onSegmentChange(value)}
      value={isLoading || isSegmentDisabled ? null : params.segment_id || null}
      textFieldReadOnly
      data-test="retargeting-segment-input"
    />
  );

  return (
    <DrawerTopBox>
      <Grid
        container
        spacing={1}
        sx={{
          alignItems: 'flex-end',
          flexGrow: 1,
        }}
      >
        <Grid size={{ xs: 12, md: 3 }}>
          <AutoComplete<RetargetingDisposition, false, false, false>
            key={`retargeting-designation-${params.disposition}-${params.segment_id}`}
            textFieldProps={{
              label: t('Designation'),
              placeholder: t('[ Select Designation ]'),
            }}
            options={retargetingDesignationOptions}
            fullWidth
            renderOptions={{
              dataTest: 'retargeting-designation-option',
            }}
            getOptionLabel={option => retargetingDesignationLabels[option]}
            onChange={(_e, value) => onDesignationChange(value ?? '')}
            value={params.disposition || null}
            textFieldReadOnly
            data-test="retargeting-designation-input"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 'grow' }} sx={{ minWidth: 0 }}>
          {isSegmentDisabled ? (
            <Tooltip title={t('Select a Designation to see the available segments.')} placement="top">
              <Box component="span" sx={{ display: 'block', width: '100%' }}>
                {segmentAutocomplete}
              </Box>
            </Tooltip>
          ) : (
            segmentAutocomplete
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 'auto' }}>
          <Button
            data-test="create-retargeting-segment-button"
            sx={{ marginTop: 1 }}
            onClick={() => window.open(AQUA_UI_CREATE_RETARGETING_SEGMENT_PATH, '_blank', 'noopener,noreferrer')}
          >
            {t('Create new segment')}
          </Button>
        </Grid>
      </Grid>
    </DrawerTopBox>
  );
};
